import {
  CALLABLE_NAMES,
  type BillingErrorCode,
  type CancelMinuteTopupRequest,
  type CancelMinuteTopupResponse,
  type CompleteBillingPaymentSetupRequest,
  type CompleteBillingPaymentSetupResponse,
  type CreateBillingPaymentSetupRequest,
  type CreateBillingPaymentSetupResponse,
  type CreateMinuteTopupRequest,
  type CreateMinuteTopupResponse,
  type CheckIncludedMinutesRequest,
  type PrepareActivationRequest,
  type PrepareActivationResponse,
  type CheckIncludedMinutesResponse,
  type GetBillingOverviewRequest,
  type GetBillingOverviewResponse,
  type ListBillingHistoryRequest,
  type ListBillingHistoryResponse,
  type QuoteMinuteTopupRequest,
  type QuoteMinuteTopupResponse,
  type SaveAutoTopupSettingsRequest,
  type SaveAutoTopupSettingsResponse,
  type SaveBillingSmsSettingsRequest,
  type SaveBillingSmsSettingsResponse,
  type SyncMinuteTopupRequest,
  type SyncMinuteTopupResponse,
} from '@acs/shared';
import { call, withoutUndefined } from './callable.js';

/**
 * Prepaid billing data access (doc 42 §13). Every money fact is read from and
 * written through a callable — the browser never writes a balance, a card, a
 * consent or a billing-SMS setting directly (the rules forbid it too).
 */

export { withoutUndefined };

export const getBillingOverview = call<GetBillingOverviewRequest, GetBillingOverviewResponse>(CALLABLE_NAMES.getBillingOverview);
export const prepareActivation = call<PrepareActivationRequest, PrepareActivationResponse>(CALLABLE_NAMES.prepareActivation);
export const checkIncludedMinutes = call<CheckIncludedMinutesRequest, CheckIncludedMinutesResponse>(CALLABLE_NAMES.checkIncludedMinutes);
export const quoteMinuteTopup = call<QuoteMinuteTopupRequest, QuoteMinuteTopupResponse>(CALLABLE_NAMES.quoteMinuteTopup);
export const createMinuteTopup = call<CreateMinuteTopupRequest, CreateMinuteTopupResponse>(CALLABLE_NAMES.createMinuteTopup);
export const syncMinuteTopup = call<SyncMinuteTopupRequest, SyncMinuteTopupResponse>(CALLABLE_NAMES.syncMinuteTopup);
export const cancelMinuteTopup = call<CancelMinuteTopupRequest, CancelMinuteTopupResponse>(CALLABLE_NAMES.cancelMinuteTopup);
export const createBillingPaymentSetup = call<CreateBillingPaymentSetupRequest, CreateBillingPaymentSetupResponse>(
  CALLABLE_NAMES.createBillingPaymentSetup,
);
export const completeBillingPaymentSetup = call<CompleteBillingPaymentSetupRequest, CompleteBillingPaymentSetupResponse>(
  CALLABLE_NAMES.completeBillingPaymentSetup,
);
export const saveAutoTopupSettings = call<SaveAutoTopupSettingsRequest, SaveAutoTopupSettingsResponse>(CALLABLE_NAMES.saveAutoTopupSettings);
export const saveBillingSmsSettings = call<SaveBillingSmsSettingsRequest, SaveBillingSmsSettingsResponse>(CALLABLE_NAMES.saveBillingSmsSettings);
export const listBillingHistory = call<ListBillingHistoryRequest, ListBillingHistoryResponse>(CALLABLE_NAMES.listBillingHistory);

/** The safe `details` a billing callable attaches to its errors. */
export function billingErrorDetails(error: unknown): { code: BillingErrorCode | null; fields: Record<string, string> } {
  const d = (error as { details?: unknown } | null)?.details as { code?: string; fields?: Record<string, string> } | undefined;
  return { code: (d?.code as BillingErrorCode | undefined) ?? null, fields: d?.fields ?? {} };
}

/**
 * The dedupe key for one purchase (doc 42 §8.2): the quote being bought. A
 * double click, a retry after a network error, or the same quote submitted
 * from a second tab all name ONE order; a fresh quote is a fresh purchase.
 * Quote ids are `q_<uuid>`, already inside the server's allowed alphabet.
 */
export function purchaseRequestId(quoteId: string): string {
  return quoteId.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
}
